import type { SSEMessage } from '@/_shared/types';

export interface TurnSlice {
  turnId: string | null;
  startedAt: number | null;
  messages: SSEMessage[];
  status: 'active' | 'completed' | 'errored' | 'aborted';
}

export function sliceIntoTurns(messages: SSEMessage[]): TurnSlice[] {
  if (messages.length === 0) return [];

  const slices: TurnSlice[] = [];
  let current: TurnSlice | null = null;

  for (const msg of messages) {
    if (msg.type === 'turn_start') {
      // Close any preamble or prior slice
      if (current) {
        if (current.status === 'active') current.status = 'completed';
        slices.push(current);
      }
      current = {
        turnId: msg.turnId,
        startedAt: msg.startedAt,
        messages: [msg],
        status: 'active',
      };
    } else if (current) {
      current.messages.push(msg);
      if (msg.type === 'error' && current.status === 'active') {
        current.status = 'errored';
      } else if (msg.type === 'turn_complete' && current.status === 'active') {
        current.status = 'completed';
      } else if (msg.type === 'turn_aborted' && current.status === 'active') {
        current.status = 'aborted';
      }
    } else {
      // No turn_start yet — preamble
      current = {
        turnId: null,
        startedAt: null,
        messages: [msg],
        status: 'completed',
      };
    }
  }

  if (current) slices.push(current);
  return slices;
}

/**
 * Remove all events belonging to a specific turn from a messages array.
 * Used to exclude active-turn events from chat.get data when the SSE stream
 * is the single source of truth for that turn.
 */
export function filterOutTurn(messages: SSEMessage[], turnId: string): SSEMessage[] {
  let inTurn = false;
  // sweepStaleTurns appends both error + turn_complete. After seeing the error
  // we still need to consume the trailing turn_complete for the same turn.
  let consumeNextComplete = false;
  return messages.filter((m) => {
    if (m.type === 'turn_start' && m.turnId === turnId) {
      inTurn = true;
      return false;
    }
    if (m.type === 'turn_complete' && m.turnId === turnId && (inTurn || consumeNextComplete)) {
      inTurn = false;
      consumeNextComplete = false;
      return false;
    }
    if (inTurn && m.type === 'error') {
      inTurn = false;
      consumeNextComplete = true;
      return false;
    }
    if (inTurn && m.type === 'turn_aborted') {
      inTurn = false;
      return false;
    }
    return !inTurn;
  });
}
